import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Spin } from "antd";
import { SwapOutlined } from "@ant-design/icons";
import { useBannerContext } from "../../context/BannerContext/BannerContext";
import { BannerListItem } from "../../context/BannerContext/constants";
import {
	Container,
	HeaderComponent,
	SubmitButtonOverlay,
} from "../../components/styled/common";
import {
	OrderItem,
	OrderItemLabel,
	SwapButton,
} from "../../components/styled/order";
import MainButtonComponent from "../../components/MainButtonComponent";


const OrderBanner = () => {
	const { getBannerList, bannerList, operationStatus, updateBannerOrders } = useBannerContext();
	const [orderedList, setOrderedList] = useState<BannerListItem[]>([]);
	const [isAnimating, setIsAnimating] = useState(false);
	const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
	const previousPositionsRef = useRef<Record<string, number>>({});
	const animationTimeoutRef = useRef<number | null>(null);

	useEffect(() => {
		getBannerList();
	}, []);

	useEffect(() => {
		if (bannerList && bannerList.length > 0) {
			const sorted = [...bannerList].sort(
				(a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
			);
			setOrderedList(sorted);
		}
	}, [bannerList]);

	useLayoutEffect(() => {
		const nextPositions: Record<string, number> = {};
		let hasMovement = false;

		orderedList.forEach((banner) => {
			const key = String(banner.bannerId);
			const element = itemRefs.current[key];
			if (!element) return;

			const currentTop = element.getBoundingClientRect().top;
			nextPositions[key] = currentTop;

			const previousTop = previousPositionsRef.current[key];
			if (previousTop === undefined) return;

			const deltaY = previousTop - currentTop;
			if (deltaY === 0) return;

			hasMovement = true;
			element.style.transition = "none";
			element.style.transform = `translateY(${deltaY}px)`;

			requestAnimationFrame(() => {
				element.style.transition = "transform 220ms ease";
				element.style.transform = "translateY(0)";
			});
		});

		if (animationTimeoutRef.current) {
			window.clearTimeout(animationTimeoutRef.current);
		}

		if (hasMovement) {
			setIsAnimating(true);
			animationTimeoutRef.current = window.setTimeout(() => {
				setIsAnimating(false);
			}, 230);
		} else {
			setIsAnimating(false);
		}

		previousPositionsRef.current = nextPositions;

		return () => {
			if (animationTimeoutRef.current) {
				window.clearTimeout(animationTimeoutRef.current);
			}
		};
	}, [orderedList]);

	const handleSwap = (index: number) => {
		if (isAnimating) return;
		if (index >= orderedList.length - 1) return;
		const newList = [...orderedList];
		const temp = newList[index];
		newList[index] = newList[index + 1];
		newList[index + 1] = temp;
		setOrderedList(newList);
	};

	const handleSave = async () => {
		updateBannerOrders(orderedList);
	};

	return (
		<Container>
			<Spin spinning={operationStatus.type === "loading" && operationStatus.operation === "fetch"}>
				<HeaderComponent>Banner Sıralama</HeaderComponent>
				{orderedList.map((banner, index) => (
					<OrderItem
						key={banner.bannerId}
						ref={(element) => {
							itemRefs.current[String(banner.bannerId)] = element;
						}}
					>
						<OrderItemLabel>
							{index + 1} - {banner.headerName}
						</OrderItemLabel>
						<SwapButton
							$isAnimating={isAnimating}
							onClick={(event) => {
								event.currentTarget.blur();
								handleSwap(index);
							}}
							disabled={isAnimating || index >= orderedList.length - 1}
							title={isAnimating || index >= orderedList.length - 1 ? undefined : "Aşağıdaki banner ile değiştir"}
						>
							<SwapOutlined rotate={90} />
						</SwapButton>
					</OrderItem>
				))}
				<SubmitButtonOverlay>
					<MainButtonComponent type="default" onClick={handleSave} loading={operationStatus.type === "loading" && operationStatus.operation === "update"}>
						Kaydet
					</MainButtonComponent>
				</SubmitButtonOverlay>
			</Spin>
		</Container>
	);
};

export default OrderBanner;
